import React, { useState, useEffect, useRef } from 'react';

interface AssignmentCommentInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    rows?: number;
    className?: string;
}

const AssignmentCommentInput: React.FC<AssignmentCommentInputProps> = ({
    value: propValue,
    onChange,
    placeholder = '',
    rows = 3,
    className = ''
}) => {
    // Local state for immediate responsiveness
    const [localValue, setLocalValue] = useState(propValue);

    // Track the last value we sent to parent to avoid re-sync loops
    const lastSentValueRef = useRef<string>(propValue);

    // Track if this is the first mount
    const isFirstMountRef = useRef(true);

    // Sync local state if prop changes externally (e.g. form reset)
    // But ignore if the change came from our own onChange call
    useEffect(() => {
        const incomingValue = propValue;

        // On first mount, always sync
        if (isFirstMountRef.current) {
            isFirstMountRef.current = false;
            setLocalValue(incomingValue);
            lastSentValueRef.current = incomingValue;
            return;
        }

        // Only sync if the incoming value is different from what we last sent
        // This means it's a true external update (like a form reset)
        if (incomingValue !== lastSentValueRef.current) {
            setLocalValue(incomingValue);
            lastSentValueRef.current = incomingValue;
        }
    }, [propValue]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setLocalValue(e.target.value);
    };

    const handleBlur = () => {
        // Only update parent if value has changed
        if (localValue !== propValue) {
            lastSentValueRef.current = localValue;
            onChange(localValue);
        }
    };

    return (
        <textarea
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            rows={rows}
            className={className}
        />
    );
};

export default React.memo(AssignmentCommentInput);
